/**
 * Non-diegetic cues and the menus: what the game tells the player rather than what the world
 * does. Square waves with a little sample-and-hold crunch - the SID chip's descendants (07 §1).
 * They play on the UI bus: never panned, never muffled by arctic air.
 */

import type { Layer } from '../synth/patch';
import { PRIORITY, type SfxDef } from './types';

const blip = (at: number, freq: number, hold: number, decay: number, gain = 1, duty = 0.25): Layer => ({
  wave: 'square', duty, at, freq, attack: 0.004, hold, decay, gain, crush: 11025, filter: { type: 'lp', cutoff: 4500 },
});

const cue = (layers: Layer[], gain: number, priority: number = PRIORITY.critical): SfxDef => ({
  patch: { layers }, variants: 1, priority, gain,
});

export const CUE_SFX = {
  /** Alert 1: somebody is wondering. Low and soft - a raised eyebrow. */
  alert1: cue([
    { wave: 'triangle', freq: 196, attack: 0.01, hold: 0.09, decay: 0.08, gain: 1 },
    { wave: 'triangle', at: 0.13, freq: 233.08, attack: 0.01, hold: 0.1, decay: 0.3, gain: 1 },
  ], 0.3),
  /** Alert 2: they are looking for you. Three rising notes of the minor triad. */
  alert2: cue([blip(0, 293.66, 0.07, 0.03), blip(0.1, 349.23, 0.07, 0.03), blip(0.2, 440, 0.1, 0.3)], 0.3),
  /** Alert 3: the garrison knows. A dissonant stab with a falling filter. */
  alert3: cue([
    { wave: 'saw', freq: 293.66, attack: 0.004, hold: 0.12, decay: 0.5, gain: 1,
      filter: { type: 'lp', cutoff: 4200, cutoffEnd: 700 } },
    { wave: 'saw', freq: 311.13, attack: 0.004, hold: 0.12, decay: 0.5, gain: 0.8,
      filter: { type: 'lp', cutoff: 4200, cutoffEnd: 700 } },
    { wave: 'saw', freq: 440, attack: 0.004, hold: 0.12, decay: 0.5, gain: 0.8,
      filter: { type: 'lp', cutoff: 4200, cutoffEnd: 700 } },
    { wave: 'saw', freq: 73.42, attack: 0.004, hold: 0.2, decay: 0.6, gain: 1, filter: { type: 'lp', cutoff: 600 } },
    { wave: 'noise', decay: 0.09, curve: 3, gain: 0.6, filter: { type: 'bp', cutoff: 1900, q: 0.9 } },
  ], 0.42),
  /** A soldier has seen the ranger: the sharp "!" - a minor second, high. */
  spotted: cue([
    blip(0, 880, 0.05, 0.16, 1, 0.125),
    blip(0, 932.33, 0.05, 0.16, 0.8, 0.125),
    { wave: 'noise', decay: 0.015, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 3000 } },
  ], 0.34, PRIORITY.action),
  /** A burst of radio: the Osprey acknowledging the recall (07 §3: filtered noise, no words). */
  radio: cue([
    { wave: 'noise', decay: 0.012, curve: 3, gain: 0.8, filter: { type: 'hp', cutoff: 2500 } },
    { wave: 'noise', at: 0.02, attack: 0.01, hold: 0.32, decay: 0.03, gain: 0.5,
      tremHz: 9, tremDepth: 0.85, tremSquare: true, crush: 8000, filter: { type: 'bp', cutoff: 1700, q: 2.2 } },
    { wave: 'saw', at: 0.02, freq: 140, freqEnd: 120, vibHz: 7, vibDepth: 2.5, attack: 0.01, hold: 0.32,
      decay: 0.03, gain: 0.5, tremHz: 9, tremDepth: 0.85, tremSquare: true, crush: 8000,
      filter: { type: 'bp', cutoff: 1500, q: 3 } },
    { wave: 'noise', at: 0.4, decay: 0.05, curve: 2, gain: 0.7, filter: { type: 'hp', cutoff: 2200 } },
  ], 0.3, PRIORITY.action),
  objectiveProgress: cue([blip(0, 880, 0.04, 0.03), blip(0.08, 1318.5, 0.05, 0.14)], 0.3),
  /**
   * NO EFFECT: a grenade off the armoured face of a thing (`sim/damage.ts` `refuseBlast`). It used to ride
   * the amber `pickup` blip, so being told his charge was wasted sounded exactly like being handed three
   * magazines. This is that blip's opposite - a knock on something solid, and a note that falls a semitone
   * and dies. Deliberately not an alarm: nothing has gone wrong, the explosive was simply spent for nothing.
   */
  refused: cue([
    blip(0, 233.08, 0.05, 0.03, 0.9, 0.5),
    blip(0.09, 220, 0.06, 0.16, 0.7, 0.5),
    { wave: 'noise', decay: 0.05, curve: 2.5, gain: 0.5, filter: { type: 'lp', cutoff: 900 } },
  ], 0.3, PRIORITY.action),
  /** D major where the score is D minor: the lift. */
  objectiveComplete: cue([
    blip(0, 293.66, 0.08, 0.04), blip(0.12, 440, 0.08, 0.04), blip(0.24, 587.33, 0.08, 0.04),
    blip(0.36, 739.99, 0.3, 0.5),
    { wave: 'triangle', at: 0.36, freq: 146.83, attack: 0.01, hold: 0.3, decay: 0.5, gain: 0.9 },
    { wave: 'triangle', at: 0.36, freq: 440, attack: 0.01, hold: 0.3, decay: 0.5, gain: 0.5 },
  ], 0.42),
  objectiveFailed: cue([
    { wave: 'saw', freq: 293.66, attack: 0.006, hold: 0.16, decay: 0.06, gain: 1,
      filter: { type: 'lp', cutoff: 1500 } },
    { wave: 'saw', at: 0.24, freq: 233.08, attack: 0.006, hold: 0.16, decay: 0.06, gain: 1,
      filter: { type: 'lp', cutoff: 1400 } },
    { wave: 'saw', at: 0.48, freq: 196, attack: 0.006, hold: 0.16, decay: 0.06, gain: 1,
      filter: { type: 'lp', cutoff: 1300 } },
    { wave: 'saw', at: 0.72, freq: 155.56, freqEnd: 146.83, attack: 0.006, hold: 0.4, decay: 0.7, gain: 1,
      filter: { type: 'lp', cutoff: 1200, cutoffEnd: 300 } },
    { wave: 'saw', at: 0.72, freq: 77.78, freqEnd: 73.42, attack: 0.006, hold: 0.4, decay: 0.7, gain: 0.9,
      filter: { type: 'lp', cutoff: 500 } },
  ], 0.42),

  /* ------------------------------------------------------------------------ menus */
  uiMove: cue([blip(0, 1174.7, 0.008, 0.02)], 0.2, PRIORITY.detail),
  uiConfirm: cue([blip(0, 880, 0.035, 0.02), blip(0.06, 1318.5, 0.05, 0.1)], 0.3, PRIORITY.action),
  uiBack: cue([blip(0, 659.26, 0.035, 0.02), blip(0.06, 440, 0.05, 0.1)], 0.28, PRIORITY.action),
  /** KIA. POW. MISSION ACCOMPLISHED. A rubber stamp, hard, on a desk. */
  uiStamp: {
    variants: 2, priority: PRIORITY.critical, gain: 0.7,
    patch: {
      layers: [
        { wave: 'sine', freq: 96, freqEnd: 40, sweep: 0.6, decay: 0.19, gain: 1 },
        { wave: 'noise', decay: 0.1, curve: 2.5, gain: 0.9, filter: { type: 'lp', cutoff: 1500, cutoffEnd: 300 } },
        { wave: 'noise', decay: 0.012, curve: 3, gain: 0.6, filter: { type: 'hp', cutoff: 2600 } },
      ],
      jitter: { pitch: 0.05 }, drive: 1.6, echo: { time: 0.043, gain: 0.3, count: 2 },
    },
  },
  /** One typewriter key: the briefing's text tick. */
  uiType: {
    variants: 5, priority: PRIORITY.ambient, gain: 0.22, rateSpread: 0.05, minInterval: 0.025,
    patch: {
      layers: [
        { wave: 'sine', freq: 2500, fmRatio: 1.83, fmIndex: 2, fmDecay: 0.004, decay: 0.014, curve: 3, gain: 0.8 },
        { wave: 'noise', decay: 0.012, curve: 3, gain: 0.7, filter: { type: 'hp', cutoff: 3000 } },
        { wave: 'sine', freq: 310, freqEnd: 240, decay: 0.03, gain: 0.6 },
      ],
      jitter: { pitch: 0.1, gain: 0.25 },
    },
  },
} satisfies Record<string, SfxDef>;
